/**
 * Unified color themes for web + mobile, assembled from the canonical scales
 * in ./baseColors.
 *
 * Structure of every theme object (per mode):
 *  - shared groups   primary / semantic / neutral / backgrounds / borders /
 *                    text / shadows — ONE set of values for both apps.
 *                    Formulas follow web's colorTheme.js (mobile's dark-mode
 *                    copies of these groups had inverted-ramp bugs: near-white
 *                    dark backgrounds, black-on-black rgba overlays).
 *  - shared blocks   electric + home_menu_colors (from mobile),
 *                    white/black/gray/green/red/yellow spreads, hero_background,
 *                    navbar_test, buttons, background, ui, features, dashboard
 *                    (from web). Harmless to the app that doesn't use them.
 *  - legacy aliases  flat THEME_* / COLOR_* keys, PER PLATFORM. The same key
 *                    means different things in each app (e.g. THEME_LIGHT is
 *                    light blue on web but brand orange on mobile), so these
 *                    are selected by the `platform` argument. Values are
 *                    byte-identical to each app's historical values, EXCEPT
 *                    web aliases derived from the reconciled 50-stops
 *                    (THEME_STUDY_QUESTION/PRACTICE_TEST/FLASHCARD_BACKGROUND
 *                    — consumer-free on web and pinned in the golden test).
 *                    Deprecation path: components migrate to the shared
 *                    groups, then this layer and the platform arg are deleted.
 *
 * Golden parity tests in __tests__/theme.test.js diff these objects against
 * BOTH apps' current source files; every intentional divergence is pinned
 * there, anything else failing is a regression.
 */

import { lighten, darken, rgbaString } from './colorUtils';
import { DEFAULT_PALETTE, resolveBaseForPalette, applyOverrides } from './palette';
import { getPalette, BRAND_PALETTES } from './brands';

export const ELECTRIC_COLORS = {
  blue: "#0088FF",
  green: "#00FF88",
  purple: "#8800FF",
  pink: "#FF0088",
  lime: "#88FF00",
  orange: "#FF8800"
};

// ---------------------------------------------------------------------------
// Theme builder — assembles all four theme objects from a brand palette.
// The default palette resolves L/D to the canonical base scales BY REFERENCE,
// so the default-brand themes are byte-identical (and identity-equal) to the
// pre-palette implementation. A brand palette swaps the primary/secondary/
// surface family slots (and optionally per-token overrides) before assembly.
// ---------------------------------------------------------------------------
function buildThemes(palette) {
  const { L, D } = resolveBaseForPalette(palette);

// ---------------------------------------------------------------------------
// Shared groups (one set of values for both apps)
// ---------------------------------------------------------------------------

// Dark-mode "neutral.gray" is the light ramp index-inverted (web convention),
// distinct from BASE_COLORS_DARK.gray which is its own hand-tuned dark ramp.
const INVERTED_GRAY_DARK = {
  50: L.gray[950],
  100: L.gray[900],
  200: L.gray[800],
  300: L.gray[700],
  400: L.gray[600],
  500: L.gray[500],
  600: L.gray[400],
  700: L.gray[300],
  800: L.gray[200],
  900: L.gray[100],
  950: L.gray[50],
  DEFAULT: L.gray[400]
};

const SEMANTIC = {
  light: {
    success: L.green[500],
    error: L.red[500],
    warning: L.yellow[500],
    info: L.blue[500],
  },
  dark: {
    success: L.green[400],
    error: L.red[400],
    warning: L.yellow[400],
    info: L.blue[400],
  },
};

// Both apps' key spellings are kept (web: absolute_*, mobile: *_absolute).
const NEUTRAL = {
  light: {
    white: L.neutral.white,
    black: L.neutral.black,
    gray: L.gray,
    absolute_black: L.neutral.black,
    absolute_white: L.neutral.white,
    white_absolute: L.neutral.white,
    black_absolute: L.neutral.black,
  },
  dark: {
    white: L.neutral.black,
    black: L.neutral.white,
    gray: INVERTED_GRAY_DARK,
    absolute_black: L.neutral.black,
    absolute_white: L.neutral.white,
    white_absolute: L.neutral.white,
    black_absolute: L.neutral.black,
  },
};

const BACKGROUNDS = {
  light: {
    primary: L.neutral.white,
    secondary: L.gray[50],
    tertiary: L.gray[100],
    card: rgbaString(L.neutral.white, 0.9),
    cardHover: L.neutral.white,
    modal: rgbaString(L.neutral.black, 0.4),
    overlay: rgbaString(L.neutral.white, 0.02),
    orange_gradient: `linear-gradient(90deg, ${L.orange[300]}, ${L.orange[500]})`,
    blue_gradient: `linear-gradient(90deg, ${L.blue[300]}, ${L.blue[500]})`,
    // Welcome-hero banner background. Brand-tunable (see palette.overrides): web reads
    // hero_gradient (CSS string), mobile reads hero_gradient_colors (LinearGradient array).
    // Default reproduces the historical primary-accent banner; brands without an override
    // inherit it (TrainingCamp swaps it to a dark slate scheme in brands/trainingcamp.js).
    hero_gradient: `linear-gradient(135deg, ${L.blue[500]} 0%, ${L.blue[500]} 50%, ${L.blue[500]} 100%)`,
    hero_gradient_colors: [L.blue[300], L.blue[500], L.blue[300]],
  },
  dark: {
    primary: L.gray[900],
    secondary: L.gray[800],
    tertiary: L.gray[700],
    card: rgbaString(L.neutral.white, 0.05),
    cardHover: rgbaString(L.neutral.white, 0.08),
    modal: rgbaString(L.neutral.white, 0.2),
    overlay: rgbaString(L.neutral.white, 0.02),
    orange_gradient: `linear-gradient(90deg, ${L.orange[300]}, ${L.orange[400]})`,
    blue_gradient: `linear-gradient(90deg, ${L.blue[300]}, ${L.blue[400]})`,
    // Dark variant uses the dark primary scale (D) so the default-brand dark banner is
    // unchanged — it previously read theme.primary.blue (= D in dark mode) directly.
    hero_gradient: `linear-gradient(135deg, ${D.blue[500]} 0%, ${D.blue[500]} 50%, ${D.blue[500]} 100%)`,
    hero_gradient_colors: [D.blue[300], D.blue[500], D.blue[300]],
  },
};

const BORDERS = {
  light: {
    primary: L.gray[200],
    secondary: L.gray[300],
    hover: L.gray[300],
    focus: L.blue[500],
  },
  dark: {
    primary: rgbaString(L.neutral.white, 0.1),
    secondary: rgbaString(L.neutral.white, 0.15),
    hover: rgbaString(L.neutral.white, 0.2),
    focus: L.blue[400],
  },
};

const TEXT = {
  light: {
    primary: L.gray[900],
    secondary: L.gray[600],
    tertiary: L.gray[500],
    inverse: L.neutral.white,
    brand: L.blue[600],
    // Accent color for the welcome-hero heading (the user's name). Defaults to white
    // (matches the current heading); TrainingCamp overrides it to emerald.
    hero_accent: L.neutral.white,
  },
  dark: {
    primary: L.gray[50],
    secondary: L.gray[300],
    tertiary: L.gray[400],
    inverse: L.gray[900],
    brand: L.blue[400],
    hero_accent: L.neutral.white,
  },
};

const SHADOWS = {
  light: {
    sm: `0 1px 2px 0 ${rgbaString(L.gray[900], 0.05)}`,
    base: `0 1px 3px 0 ${rgbaString(L.gray[900], 0.1)}, 0 1px 2px 0 ${rgbaString(L.gray[900], 0.06)}`,
    md: `0 4px 6px -1px ${rgbaString(L.gray[900], 0.1)}, 0 2px 4px -1px ${rgbaString(L.gray[900], 0.06)}`,
    lg: `0 10px 15px -3px ${rgbaString(L.gray[900], 0.1)}, 0 4px 6px -2px ${rgbaString(L.gray[900], 0.05)}`,
    xl: `0 20px 25px -5px ${rgbaString(L.gray[900], 0.1)}, 0 10px 10px -5px ${rgbaString(L.gray[900], 0.04)}`,
    card: `0 4px 20px ${rgbaString(L.gray[900], 0.08)}`,
    cardHover: `0 8px 25px ${rgbaString(L.gray[900], 0.15)}`,
  },
  dark: {
    sm: `0 1px 2px 0 ${rgbaString(L.neutral.black, 0.3)}`,
    base: `0 1px 3px 0 ${rgbaString(L.neutral.black, 0.4)}, 0 1px 2px 0 ${rgbaString(L.neutral.black, 0.3)}`,
    md: `0 4px 6px -1px ${rgbaString(L.neutral.black, 0.4)}, 0 2px 4px -1px ${rgbaString(L.neutral.black, 0.3)}`,
    lg: `0 10px 15px -3px ${rgbaString(L.neutral.black, 0.4)}, 0 4px 6px -2px ${rgbaString(L.neutral.black, 0.3)}`,
    xl: `0 20px 25px -5px ${rgbaString(L.neutral.black, 0.5)}, 0 10px 10px -5px ${rgbaString(L.neutral.black, 0.4)}`,
    card: `0 4px 20px ${rgbaString(L.neutral.black, 0.3)}`,
    cardHover: `0 8px 25px ${rgbaString(L.neutral.black, 0.4)}`,
  },
};

// ---------------------------------------------------------------------------
// Shared blocks carried over from mobile (home menu, per-feature gradients)
// ---------------------------------------------------------------------------

// Every home-menu tile follows one formula; only the surface hue and the icon
// hue vary (Flashcards and StudyArticles split the pair). Adding a tile means
// adding one row here, not hand-syncing two 9-line blocks.
// A brand's `palette.dataAccents` is MERGED over these defaults (per-feature),
// so a brand only restates the tiles it retunes — adding a new core tile later
// can't leave an existing brand with a missing `home_menu_colors.<Feature>`.
const HOME_MENU_FEATURES = {
  StudyQuestions: { hue: 'blue', iconHue: 'blue' },
  PracticeTests: { hue: 'purple', iconHue: 'purple' },
  Flashcards: { hue: 'yellow', iconHue: 'gold' },
  StudyArticles: { hue: 'green', iconHue: 'emerald' },
  CreateCustomTest: { hue: 'orange', iconHue: 'orange' },
  Glossary: { hue: 'indigo', iconHue: 'indigo' },
  Acronyms: { hue: 'pink', iconHue: 'pink' },
  ...(palette.dataAccents || {}),
};

const buildHomeMenu = (entry) => {
  const out = {};
  for (const [feature, { hue, iconHue }] of Object.entries(HOME_MENU_FEATURES)) {
    out[feature] = entry(hue, iconHue);
  }
  return out;
};

const HOME_MENU_COLORS = {
  light: buildHomeMenu((hue, iconHue) => ({
    background: L[hue][50],
    backgroundGradient: [L[hue][100], L[hue][50]],
    border: L[hue][100],
    bubble: L[hue][200],
    iconBackground: L[hue][100],
    iconGradient: [L[iconHue][300], L[iconHue][500]],
    iconColor: L.neutral.white,
  })),
  // Dark tiles share one neutral D.gray shell; only the icon colors vary.
  dark: buildHomeMenu((hue, iconHue) => ({
    background: D.gray[100],
    backgroundGradient: [D.gray[200], D.gray[100]],
    border: D.gray[100],
    bubble: D.gray[200],
    iconBackground: D[hue][100],
    iconGradient: [D[iconHue][500], D[iconHue][600]],
    iconColor: D.neutral.white,
  })),
};

// ---------------------------------------------------------------------------
// Shared blocks carried over from web (dashboard, buttons, hero, features…)
// ---------------------------------------------------------------------------

const DASHBOARD = {
  light: {
    card: {
      background: rgbaString(L.neutral.white, 0.9),
      backgroundHover: L.neutral.white,
      border: L.gray[200],
      borderHover: L.gray[300],
      shadow: `0 4px 20px ${rgbaString(L.gray[900], 0.08)}`,
      shadowHover: `0 8px 25px ${rgbaString(L.gray[900], 0.15)}`,
    },
    review: {
      itemBackground: L.gray[50],
      itemBackgroundHover: L.gray[100],
      badge: {
        all: {
          background: rgbaString(L.blue[500], 0.1),
          text: L.blue[700],
          border: rgbaString(L.blue[500], 0.3),
        },
        correct: {
          background: rgbaString(L.green[500], 0.1),
          text: L.green[700],
          border: rgbaString(L.green[500], 0.3),
        },
        incorrect: {
          background: rgbaString(L.red[500], 0.1),
          text: L.red[700],
          border: rgbaString(L.red[500], 0.3),
        },
        unanswered: {
          background: rgbaString(L.yellow[500], 0.1),
          text: L.yellow[700],
          border: rgbaString(L.yellow[500], 0.3),
        },
      },
    },
    domain: {
      cardBackground: L.gray[50],
      cardBackgroundHover: L.neutral.white,
      progressTrack: L.gray[200],
      progress: {
        critical: `linear-gradient(90deg, ${L.red[500]}, ${L.red[400]})`,
        warning: `linear-gradient(90deg, ${L.yellow[500]}, ${L.yellow[400]})`,
        success: `linear-gradient(90deg, ${L.green[500]}, ${L.green[400]})`,
      },
      border: {
        critical: L.red[500],
        warning: L.yellow[500],
        success: L.green[500],
      },
    },
    score: {
      background: L.gray[100],
      gradient: `linear-gradient(90deg, ${L.red[500]}, ${L.red[400]})`,
      dropShadow: rgbaString(L.red[500], 0.2),
    },
    button: {
      primary: {
        background: `linear-gradient(135deg, ${L.blue[500]} 0%, ${L.blue[600]} 100%)`,
        shadow: `0 4px 15px ${rgbaString(L.blue[500], 0.3)}`,
        shadowHover: `0 8px 25px ${rgbaString(L.blue[500], 0.4)}`,
      },
      secondary: {
        background: L.neutral.white,
        border: L.gray[200],
        borderHover: L.gray[300],
        text: L.gray[600],
        textHover: L.gray[900],
        shadow: `0 1px 2px 0 ${rgbaString(L.gray[900], 0.05)}`,
        shadowHover: `0 4px 6px -1px ${rgbaString(L.gray[900], 0.1)}`,
      },
    },
    status: {
      correct: L.green[500],
      incorrect: L.red[500],
      unanswered: L.yellow[500],
      primary: L.blue[600],
    },
  },
  dark: {
    card: {
      background: rgbaString(L.neutral.white, 0.05),
      backgroundHover: rgbaString(L.neutral.white, 0.08),
      border: rgbaString(L.neutral.white, 0.1),
      borderHover: rgbaString(L.neutral.white, 0.2),
      shadow: `0 4px 20px ${rgbaString(L.neutral.black, 0.3)}`,
      shadowHover: `0 8px 25px ${rgbaString(L.neutral.black, 0.4)}`,
    },
    review: {
      itemBackground: L.gray[800],
      itemBackgroundHover: L.gray[700],
      badge: {
        all: {
          background: rgbaString(L.blue[400], 0.1),
          text: L.blue[400],
          border: rgbaString(L.blue[400], 0.3),
        },
        correct: {
          background: rgbaString(L.green[400], 0.1),
          text: L.green[400],
          border: rgbaString(L.green[400], 0.3),
        },
        incorrect: {
          background: rgbaString(L.red[400], 0.1),
          text: L.red[400],
          border: rgbaString(L.red[400], 0.3),
        },
        unanswered: {
          background: rgbaString(L.yellow[400], 0.1),
          text: L.yellow[400],
          border: rgbaString(L.yellow[400], 0.3),
        },
      },
    },
    domain: {
      cardBackground: L.gray[800],
      cardBackgroundHover: rgbaString(L.neutral.white, 0.08),
      progressTrack: rgbaString(L.neutral.white, 0.1),
      progress: {
        critical: `linear-gradient(90deg, ${L.red[400]}, ${L.red[300]})`,
        warning: `linear-gradient(90deg, ${L.yellow[400]}, ${L.yellow[300]})`,
        success: `linear-gradient(90deg, ${L.green[400]}, ${L.green[300]})`,
      },
      border: {
        critical: L.red[400],
        warning: L.yellow[400],
        success: L.green[400],
      },
    },
    score: {
      background: rgbaString(L.neutral.white, 0.1),
      gradient: `linear-gradient(90deg, ${L.red[400]}, ${L.red[300]})`,
      dropShadow: rgbaString(L.red[400], 0.3),
    },
    button: {
      primary: {
        background: `linear-gradient(135deg, ${L.blue[400]} 0%, ${L.blue[500]} 100%)`,
        shadow: `0 4px 15px ${rgbaString(L.blue[400], 0.3)}`,
        shadowHover: `0 8px 25px ${rgbaString(L.blue[400], 0.4)}`,
      },
      secondary: {
        background: rgbaString(L.neutral.white, 0.05),
        border: rgbaString(L.neutral.white, 0.1),
        borderHover: rgbaString(L.neutral.white, 0.15),
        text: L.gray[300],
        textHover: L.gray[50],
        shadow: `0 1px 2px 0 ${rgbaString(L.neutral.black, 0.3)}`,
        shadowHover: `0 4px 6px -1px ${rgbaString(L.neutral.black, 0.4)}`,
      },
    },
    status: {
      correct: L.green[400],
      incorrect: L.red[400],
      unanswered: L.yellow[400],
      primary: L.blue[400],
    },
  },
};

// Non-color metrics carried with the web theme for back-compat.
const BUTTON_SIZES = {
  sm: {
    padding: '0.5rem 1rem',
    fontSize: '0.875rem',
    borderRadius: '0.5rem',
  },
  md: {
    padding: '0.75rem 1.5rem',
    fontSize: '1rem',
    borderRadius: '0.75rem',
  },
  lg: {
    padding: '1rem 2rem',
    fontSize: '1.125rem',
    borderRadius: '0.75rem',
  }
};

const BUTTONS = {
  light: {
    primary: {
      background: `linear-gradient(135deg, ${L.blue[500]} 0%, ${L.blue[600]} 100%)`,
      backgroundHover: `linear-gradient(135deg, ${L.blue[600]} 0%, ${L.blue[700]} 100%)`,
      backgroundActive: `linear-gradient(135deg, ${L.blue[700]} 0%, ${L.blue[800]} 100%)`,
      backgroundDisabled: L.gray[300],
      text: L.neutral.white,
      textDisabled: L.gray[500],
      border: L.blue[600],
      borderHover: L.blue[700],
      shadow: `0 4px 12px ${rgbaString(L.blue[600], 0.3)}`,
      shadowHover: `0 6px 20px ${rgbaString(L.blue[600], 0.4)}`,
    },
    secondary: {
      background: `linear-gradient(135deg, ${L.orange[400]} 0%, ${L.orange[500]} 100%)`,
      backgroundHover: `linear-gradient(135deg, ${L.orange[500]} 0%, ${L.orange[600]} 100%)`,
      backgroundActive: `linear-gradient(135deg, ${L.orange[600]} 0%, ${L.orange[700]} 100%)`,
      backgroundDisabled: L.gray[300],
      text: L.neutral.white,
      textDisabled: L.gray[500],
      border: L.orange[500],
      borderHover: L.orange[600],
      shadow: `0 4px 12px ${rgbaString(L.orange[500], 0.3)}`,
      shadowHover: `0 6px 20px ${rgbaString(L.orange[500], 0.4)}`,
    },
    outlinePrimary: {
      background: 'transparent',
      backgroundHover: `linear-gradient(135deg, ${L.blue[50]} 0%, ${L.blue[100]} 100%)`,
      backgroundActive: `linear-gradient(135deg, ${L.blue[100]} 0%, ${L.blue[200]} 100%)`,
      backgroundDisabled: 'transparent',
      text: L.blue[600],
      textHover: L.blue[700],
      textDisabled: L.gray[400],
      border: L.blue[300],
      borderHover: L.blue[400],
      shadow: 'none',
      shadowHover: `0 4px 12px ${rgbaString(L.blue[600], 0.1)}`,
    },
    outlineSecondary: {
      background: 'transparent',
      backgroundHover: `linear-gradient(135deg, ${L.orange[50]} 0%, ${L.orange[100]} 100%)`,
      backgroundActive: `linear-gradient(135deg, ${L.orange[100]} 0%, ${L.orange[200]} 100%)`,
      backgroundDisabled: 'transparent',
      text: L.orange[600],
      textHover: L.orange[700],
      textDisabled: L.gray[400],
      border: L.orange[300],
      borderHover: L.orange[400],
      shadow: 'none',
      shadowHover: `0 4px 12px ${rgbaString(L.orange[500], 0.1)}`,
    },
    sizes: BUTTON_SIZES,
  },
  dark: {
    primary: {
      background: `linear-gradient(135deg, ${D.blue[400]} 0%, ${D.blue[500]} 100%)`,
      backgroundHover: `linear-gradient(135deg, ${D.blue[300]} 0%, ${D.blue[400]} 100%)`,
      backgroundActive: `linear-gradient(135deg, ${D.blue[500]} 0%, ${D.blue[600]} 100%)`,
      backgroundDisabled: D.gray[700],
      text: D.neutral.white,
      textDisabled: D.gray[500],
      border: D.blue[500],
      borderHover: D.blue[400],
      shadow: `0 4px 12px ${rgbaString(L.neutral.black, 0.4)}`,
      shadowHover: `0 6px 20px ${rgbaString(D.blue[500], 0.5)}`,
    },
    secondary: {
      background: `linear-gradient(135deg, ${D.orange[400]} 0%, ${D.orange[500]} 100%)`,
      backgroundHover: `linear-gradient(135deg, ${D.orange[300]} 0%, ${D.orange[400]} 100%)`,
      backgroundActive: `linear-gradient(135deg, ${D.orange[500]} 0%, ${D.orange[600]} 100%)`,
      backgroundDisabled: D.gray[700],
      text: D.neutral.white,
      textDisabled: D.gray[500],
      border: D.orange[500],
      borderHover: D.orange[400],
      shadow: `0 4px 12px ${rgbaString(L.neutral.black, 0.4)}`,
      shadowHover: `0 6px 20px ${rgbaString(D.orange[500], 0.5)}`,
    },
    outlinePrimary: {
      background: 'transparent',
      backgroundHover: `linear-gradient(135deg, ${rgbaString(D.blue[500], 0.1)} 0%, ${rgbaString(D.blue[400], 0.15)} 100%)`,
      backgroundActive: `linear-gradient(135deg, ${rgbaString(D.blue[500], 0.2)} 0%, ${rgbaString(D.blue[400], 0.25)} 100%)`,
      backgroundDisabled: 'transparent',
      text: D.blue[400],
      textHover: D.blue[300],
      textDisabled: D.gray[600],
      border: D.blue[600],
      borderHover: D.blue[500],
      shadow: 'none',
      shadowHover: `0 4px 12px ${rgbaString(D.blue[500], 0.2)}`,
    },
    outlineSecondary: {
      background: 'transparent',
      backgroundHover: `linear-gradient(135deg, ${rgbaString(D.orange[500], 0.1)} 0%, ${rgbaString(D.orange[400], 0.15)} 100%)`,
      backgroundActive: `linear-gradient(135deg, ${rgbaString(D.orange[500], 0.2)} 0%, ${rgbaString(D.orange[400], 0.25)} 100%)`,
      backgroundDisabled: 'transparent',
      text: D.orange[400],
      textHover: D.orange[300],
      textDisabled: D.gray[600],
      border: D.orange[600],
      borderHover: D.orange[500],
      shadow: 'none',
      shadowHover: `0 4px 12px ${rgbaString(D.orange[500], 0.2)}`,
    },
    sizes: BUTTON_SIZES,
  },
};

// ---------------------------------------------------------------------------
// Per-mode shared theme (everything except the per-platform legacy aliases)
// ---------------------------------------------------------------------------

const SHARED_LIGHT = {
  primary: L,
  semantic: SEMANTIC.light,
  neutral: NEUTRAL.light,
  backgrounds: BACKGROUNDS.light,
  borders: BORDERS.light,
  text: TEXT.light,
  shadows: SHADOWS.light,

  electric: ELECTRIC_COLORS,
  home_menu_colors: HOME_MENU_COLORS.light,

  white: NEUTRAL.light.white,
  black: NEUTRAL.light.black,
  gray: NEUTRAL.light.gray,
  green: L.green,
  red: L.red,
  yellow: L.yellow,

  hero_background: {
    0: L.blue[50],
    15: L.blue[100],
    30: lighten(L.blue[200], 5),
    45: L.blue[200],
    60: lighten(L.blue[300], 5),
    75: L.blue[300],
    90: lighten(L.blue[400], 5),
    100: L.blue[400]
  },

  navbar_test: {
    background: L.blue[500],
    text: L.neutral.black,
    progress_bar: L.blue[200],
    progress_bar_fill: L.blue[400],
    dot_color: L.neutral.black,
    end_button: L.red[500],
    end_button_hover: L.red[200],
    end_button_text: L.neutral.black,
    arrow_background: L.orange[500],
  },

  buttons: BUTTONS.light,

  background: {
    main: L.neutral.white,
    primary: L.neutral.white,
    secondary: L.gray[50],
    tertiary: L.gray[100],
    orange: rgbaString(L.orange[500], 0.15),
    blue: L.blue[50],
    input: L.gray[50],
    card: L.gray[50],
    separator: L.gray[200],
    modal: BACKGROUNDS.light.modal,
    premium: lighten(L.orange[100], 10),
    orange_gradient: BACKGROUNDS.light.orange_gradient,
    blue_gradient: BACKGROUNDS.light.blue_gradient,
  },

  ui: {
    primary: L.blue[500],
    secondary: L.orange[500],
    success: SEMANTIC.light.success,
    warning: SEMANTIC.light.warning,
    error: SEMANTIC.light.error,
    border: BORDERS.light.primary,
  },

  features: {
    study: {
      background: L.blue[50],
      icon: L.blue[500],
    },
    practice: {
      background: L.yellow[50],
      icon: L.yellow[600],
    },
    flashcard: {
      background: L.green[50],
      icon: L.green[600],
    },
    custom: {
      background: L.red[50],
      icon: L.red[600],
    },
  },

  dashboard: DASHBOARD.light,
};

const SHARED_DARK = {
  primary: D,
  semantic: SEMANTIC.dark,
  neutral: NEUTRAL.dark,
  backgrounds: BACKGROUNDS.dark,
  borders: BORDERS.dark,
  text: TEXT.dark,
  shadows: SHADOWS.dark,

  electric: ELECTRIC_COLORS,
  home_menu_colors: HOME_MENU_COLORS.dark,

  white: NEUTRAL.dark.white,
  black: NEUTRAL.dark.black,
  gray: NEUTRAL.dark.gray,
  green: D.green,
  red: D.red,
  yellow: D.yellow,

  hero_background: {
    0: D.blue[200],
    15: D.blue[300],
    30: D.blue[400],
    45: D.blue[500],
    60: D.blue[600],
    75: D.blue[700],
    90: D.blue[800],
    100: D.blue[900]
  },

  navbar_test: {
    background: L.blue[500],
    text: NEUTRAL.dark.black,
    progress_bar: D.blue[200],
    progress_bar_fill: D.blue[400],
    dot_color: NEUTRAL.dark.black,
    end_button: D.red[400],
    end_button_hover: D.red[700],
    end_button_text: NEUTRAL.dark.black,
    arrow_background: L.orange[500],
  },

  buttons: BUTTONS.dark,

  background: {
    main: L.neutral.black,
    primary: L.neutral.black,
    secondary: L.gray[900],
    tertiary: L.gray[800],
    orange: rgbaString(L.orange[500], 0.25),
    blue: rgbaString(L.blue[500], 0.4),
    input: BACKGROUNDS.dark.secondary,
    card: L.gray[800],
    separator: BACKGROUNDS.dark.tertiary,
    modal: BACKGROUNDS.dark.modal,
    premium: darken(D.orange[100], 10),
    orange_gradient: BACKGROUNDS.dark.orange_gradient,
    blue_gradient: BACKGROUNDS.dark.blue_gradient,
  },

  ui: {
    primary: L.blue[500],
    secondary: L.orange[500],
    success: SEMANTIC.dark.success,
    warning: SEMANTIC.dark.warning,
    error: SEMANTIC.dark.error,
    border: BORDERS.dark.primary,
  },

  features: {
    study: {
      background: BACKGROUNDS.dark.secondary,
      icon: D.blue[400],
    },
    practice: {
      background: BACKGROUNDS.dark.secondary,
      icon: D.yellow[400],
    },
    flashcard: {
      background: BACKGROUNDS.dark.secondary,
      icon: D.green[400],
    },
    custom: {
      background: BACKGROUNDS.dark.secondary,
      icon: D.red[400],
    },
  },

  dashboard: DASHBOARD.dark,
};

// ---------------------------------------------------------------------------
// Legacy alias layers — PER PLATFORM. Byte-identical to each app's historical
// values except where noted in the file header. Values that equal a canonical
// stop reference the token (so a future brand retune moves everything
// together); raw literals remain only for true one-offs with no scale
// equivalent. New code must use the shared groups above instead.
// ---------------------------------------------------------------------------

const WEB_ALIASES = {
  light: {
    THEME_DARK: L.blue[500],
    THEME_LIGHT: L.blue[100],
    THEME_TEXT_BLACK: L.neutral.black,
    THEME_TEXT_BLACK_LIGHT: TEXT.light.secondary,
    THEME_TEXT_GRAY: L.gray[500],
    NORMAL_COLOR_WHITE: L.neutral.white,
    THEME_SCREEN_BACKGROUND_COLOR: BACKGROUNDS.light.primary,
    THEME_INPUT_BACKGROUND: L.gray[50],
    THEME_SEPERATER: L.gray[200],
    THEME_SCORE_CONTAINER: L.orange[500],
    THEME_PREMIUM_BACKGROUND: lighten(L.orange[100], 10),
    THEME_STUDY_QUESTION_BACKGROUND: L.blue[50],
    THEME_PRACTICE_TEST_BACKGROUND: L.yellow[50],
    THEME_FLASHCARD_BACKGROUND: L.green[50],
    THEME_CUSTOM_TEST_BACKGROUND: L.red[50],
    THEME_STUDY_ICON_BACKGROUND_COLOR: L.blue[500],
    THEME_PRACTICE_TEST_ICON_BACKGROUND_COLOR: L.yellow[600],
    THEME_FLASHCARD_ICON_BACKGROUND_COLOR: L.green[600],
    THEME_CUSTOM_ICON_BACKGROUND_COLOR: L.red[600],
  },
  dark: {
    THEME_DARK: L.blue[500],
    THEME_LIGHT: D.blue[100],
    THEME_TEXT_BLACK: NEUTRAL.dark.black,
    THEME_TEXT_BLACK_LIGHT: TEXT.dark.secondary,
    THEME_TEXT_GRAY: D.gray[500],
    NORMAL_COLOR_WHITE: NEUTRAL.dark.white,
    THEME_SCREEN_BACKGROUND_COLOR: BACKGROUNDS.dark.primary,
    THEME_INPUT_BACKGROUND: BACKGROUNDS.dark.secondary,
    THEME_SEPERATER: BACKGROUNDS.dark.tertiary,
    THEME_SCORE_CONTAINER: L.orange[500],
    THEME_PREMIUM_BACKGROUND: D.gray[800],
    THEME_STUDY_QUESTION_BACKGROUND: BACKGROUNDS.dark.secondary,
    THEME_PRACTICE_TEST_BACKGROUND: BACKGROUNDS.dark.secondary,
    THEME_FLASHCARD_BACKGROUND: BACKGROUNDS.dark.secondary,
    THEME_CUSTOM_TEST_BACKGROUND: BACKGROUNDS.dark.secondary,
    THEME_STUDY_ICON_BACKGROUND_COLOR: D.blue[600],
    THEME_PRACTICE_TEST_ICON_BACKGROUND_COLOR: D.yellow[600],
    THEME_FLASHCARD_ICON_BACKGROUND_COLOR: D.green[600],
    THEME_CUSTOM_ICON_BACKGROUND_COLOR: D.red[600],
  },
};

const MOBILE_ALIASES = {
  light: {
    NORMAL_COLOR_WHITE: '#fff',
    APP_BACKGROUND_COLOR: '#F8FAFD',

    THEME_DARK: L.blue[500],
    THEME_LIGHT: L.orange[500],
    THEME_SCORE_CONTAINER: "#0868a7",
    THEME_INPUT_BACKGROUND: '#F6FAFF',
    THEME_SEPERATER: '#E1E8F2',
    THEME_FLASH_CARDS_SEPERATER: '#E1E8F2',
    THEME_PREMIUM_BACKGROUND: L.blue[60],

    THEME_TUTORIAL_BACKGROUND: "#FAF1ED",
    THEME_TUTORIAL_DETAILS_BACKGROUND: '#fff',

    COLOR_GREEN: '#05C46B',
    COLOR_YELLOW: '#f7cd46',
    COLOR_RED: '#F53B57',

    BLUE_GREDIENT: ['#B6D3FF', '#448FFF'],
    ORANGE_GREDIENT: ['#FFCDB5', '#F68C59'],

    TIMER_20_SECOND_BACKGROUND: '#F53B57',
    TIMER_30_SECOND_BACKGROUND: '#060E19',
    TIMER_BACKGROUND: '#D99000',

    THEME_SCREEN_BACKGROUND_COLOR: '#fff',
    THEME_SCREEN_BACKGROUND_ALPHA: 'rgba(0,0,0,0.5)',

    THEME_TEXT_BLACK: '#000',
    THEME_TEXT_BLACK_LIGHT: "#060E19",
    THEME_TEXT_GRAY: '#8592A6',
    THEME_STREAK_BACKGROUND: '#D9D9D9',
    TEXT_ORANGE_COLOR: '#F68c59',
    THEME_TEXT_WHITE: '#fff',
    THEME_TEXT_LIMK_COLOR: '#164CFF',

    TABBAR_BACKGROUND: "#fff",
    TABBAR_LANDSCAPE_BG: '#F5F5F7',
    TAABAR_UNSELECTED_COLOR: "#999999",
    HEADER_ICON_BORDER: "rgba(255,255,255,0.1)",
    THEMER_FILTER_ITEM_BACKGROUND: "#F6FAFF",

    THEME_STUDY_QUESTION_BACKGROUND: "#EDF4FF",
    THEME_QOD_BACKGROUND: "#FFE9C1",
    THEME_PRACTICE_TEST_BACKGROUND: "#FFF9DF",
    THEME_FLASHCARD_BACKGROUND: "#E6FAF1",
    THEME_CUSTOM_TEST_BACKGROUND: "#FEECEF",
    THEME_STUDY_ICON_BACKGROUND_COLOR: '#448FFF',
    THEME_PRACTICE_TEST_ICON_BACKGROUND_COLOR: '#FFD32A',
    THEME_FLASHCARD_ICON_BACKGROUND_COLOR: '#05C46B',
    THEME_CUSTOM_ICON_BACKGROUND_COLOR: '#F53B57',
    THEME_ARTICLES_ICON_BACKGROUND_COLOR: '#9C27B0',
    THEME_ARTICLES_BACKGROUND_COLOR: '#fadeffff',

    THEME_MENU_TEXT_COLOR: '#060E19',
    THEME_BORDER_COLOR: '#C0C0C0',

    THEME_HISTORY_PROGRESS_COLOR: '#ADBACC',
    THEME_HISTORY_OVERALL_TITLE_COLOR: '#D4D4D4',
    THEME_CIRCLE_PROGRESS_BG: 'rgba(0,0,0,0.3)',
    THEME_FOTTER_BACKGROUND: '#f2f2f2',

    THEME_TEST_REVIEW_ITEM_BACKGROUND: '#EDF4FF',

    MODAL_BACKGROUND_COLOR: 'rgba(0,0,0,0.4)',

    THEME_FILTER_SELECTION: L.orange[500],
    THEME_FILTER_BACKGROUND: L.orange[500],
  },
  dark: {
    NORMAL_COLOR_WHITE: '#fff',
    APP_BACKGROUND_COLOR: L.neutral.black,

    THEME_DARK: D.gray[100],
    THEME_LIGHT: D.orange[500],
    THEME_SCORE_CONTAINER: "#18191e",
    THEME_INPUT_BACKGROUND: D.gray[100],
    THEME_SEPERATER: '#27292D',
    THEME_FLASH_CARDS_SEPERATER: 'rgba(255,255,255,0.4)',
    THEME_PREMIUM_BACKGROUND: D.gray[100],

    THEME_TUTORIAL_BACKGROUND: "#242424",
    THEME_TUTORIAL_DETAILS_BACKGROUND: '#000',

    COLOR_GREEN: '#2e9e68',
    COLOR_YELLOW: '#f2c800',
    COLOR_RED: '#a3293b',

    BLUE_GREDIENT: ['#B6D3FF', '#448FFF'],
    ORANGE_GREDIENT: ['#FFCDB5', '#F68C59'],
    QUESTION_OF_THE_DAY_BG: ['#0046AB', '#001F4C'],
    QUESTION_BOX_BG: ['rgba(45, 94, 163, 1)', '#001F4C'],
    GOTIT_BG: ['rgba(45, 94, 163, 1)', '#001F4C', '#001F4C'],

    TIMER_20_SECOND_BACKGROUND: '#fc413b',
    TIMER_30_SECOND_BACKGROUND: '#f2f2f2',
    TIMER_BACKGROUND: '#f2f2f2',

    THEME_SCREEN_BACKGROUND_COLOR: '#101010',
    THEME_SCREEN_BACKGROUND_ALPHA: 'rgba(255,255,255,0.5)',
    THEMER_FILTER_ITEM_BACKGROUND: D.gray[100],

    THEME_TEXT_BLACK: '#fff',
    THEME_TEXT_BLACK_LIGHT: "#dddddd",
    THEME_TEXT_GRAY: '#ADBACC',
    THEME_STREAK_BACKGROUND: '#D9D9D9',
    TEXT_ORANGE_COLOR: '#F68c59',
    THEME_TEXT_WHITE: '#fff',
    THEME_TEXT_LIMK_COLOR: '#164CFF',

    TABBAR_BACKGROUND: D.gray[100],
    TABBAR_LANDSCAPE_BG: D.gray[100],
    TAABAR_UNSELECTED_COLOR: "#8A8B92",
    HEADER_ICON_BORDER: "rgba(255,255,255,0.1)",

    THEME_STUDY_QUESTION_BACKGROUND: D.gray[100],
    THEME_QOD_BACKGROUND: D.gray[100],
    THEME_PRACTICE_TEST_BACKGROUND: D.gray[100],
    THEME_FLASHCARD_BACKGROUND: D.gray[100],
    THEME_CUSTOM_TEST_BACKGROUND: D.gray[100],
    THEME_STUDY_ICON_BACKGROUND_COLOR: '#1565C0',
    THEME_PRACTICE_TEST_ICON_BACKGROUND_COLOR: '#F9A825',
    THEME_FLASHCARD_ICON_BACKGROUND_COLOR: '#2E7D32',
    THEME_CUSTOM_ICON_BACKGROUND_COLOR: '#B71C1C',
    THEME_ARTICLES_ICON_BACKGROUND_COLOR: '#7B1FA2',
    THEME_ARTICLES_BACKGROUND_COLOR: D.gray[100],

    THEME_MENU_TEXT_COLOR: '#fff',
    THEME_BORDER_COLOR: '#C0C0C0',

    THEME_HISTORY_PROGRESS_COLOR: '#ADBACC',
    THEME_HISTORY_OVERALL_TITLE_COLOR: '#D4D4D4',
    THEME_CIRCLE_PROGRESS_BG: 'rgba(255,255,255,0.3)',
    THEME_FOTTER_BACKGROUND: '#242424',

    THEME_TEST_REVIEW_ITEM_BACKGROUND: D.gray[100],

    MODAL_BACKGROUND_COLOR: 'rgba(255,255,255,0.2)',

    THEME_FILTER_SELECTION: "#fff",
    THEME_FILTER_BACKGROUND: 'rgba(255,255,255,0.3)',
  },
};

// ---------------------------------------------------------------------------
// Assembled themes + accessor
// ---------------------------------------------------------------------------

// Transitional mobile-only dark overrides. These are values mobile shipped
// DELIBERATELY (unlike the inverted-ramp bugs the shared groups fix):
//  - backgrounds.card: opaque #1E1F25 — RN iOS shadow* / Android elevation
//    misrender on translucent backgrounds, and mobile cards set both together.
//  - neutral.white: #121212 soft black (Material dark surface), used as the
//    screen/card background tone across Home, Login, Register, articles, etc.
//  - text.secondary/tertiary: mobile's own dark-ramp grays — web's are one
//    stop brighter, which is a contrast retune, not a bug fix.
// Everything else in the shared groups is identical to web. Each override is
// pinned in __tests__/theme.test.js; retiring them is a deliberate restyle.
const MOBILE_DARK_OVERRIDES = {
  neutral: { ...NEUTRAL.dark, white: '#121212' },
  backgrounds: { ...BACKGROUNDS.dark, card: D.gray[100] },
  text: { ...TEXT.dark, secondary: D.gray[600], tertiary: D.gray[500] },
};

  const ov = palette.overrides;
  const LIGHT_THEME_WEB = applyOverrides(
    { ...SHARED_LIGHT, ...WEB_ALIASES.light }, ov && ov.web && ov.web.light);
  const DARK_THEME_WEB = applyOverrides(
    { ...SHARED_DARK, ...WEB_ALIASES.dark }, ov && ov.web && ov.web.dark);
  const LIGHT_THEME_MOBILE = applyOverrides(
    { ...SHARED_LIGHT, ...MOBILE_ALIASES.light }, ov && ov.mobile && ov.mobile.light);
  const DARK_THEME_MOBILE = applyOverrides(
    { ...SHARED_DARK, ...MOBILE_DARK_OVERRIDES, ...MOBILE_ALIASES.dark }, ov && ov.mobile && ov.mobile.dark);

  return { LIGHT_THEME_WEB, DARK_THEME_WEB, LIGHT_THEME_MOBILE, DARK_THEME_MOBILE };
}

// ---------------------------------------------------------------------------
// Default-brand singletons (identity-preserved) + brand-aware accessor
// ---------------------------------------------------------------------------

const DEFAULT_THEMES = buildThemes(DEFAULT_PALETTE);

export const LIGHT_THEME_WEB = DEFAULT_THEMES.LIGHT_THEME_WEB;
export const DARK_THEME_WEB = DEFAULT_THEMES.DARK_THEME_WEB;
export const LIGHT_THEME_MOBILE = DEFAULT_THEMES.LIGHT_THEME_MOBILE;
export const DARK_THEME_MOBILE = DEFAULT_THEMES.DARK_THEME_MOBILE;

// Themes are built once per brand and memoized. Unknown/default brands reuse the
// singletons above, so getColorTheme(mode) stays referentially identical to the
// pre-brand behavior (golden tests pin this).
const _themeCache = new Map([['default', DEFAULT_THEMES]]);

function themesForBrand(brand) {
  const key = brand && BRAND_PALETTES[brand] ? brand : 'default';
  if (!_themeCache.has(key)) _themeCache.set(key, buildThemes(getPalette(key)));
  return _themeCache.get(key);
}

/**
 * Drop-in replacement for both apps' theme accessors.
 *
 * web ThemeProvider:    getColorTheme(mode, 'web', brand)    // platform defaults to 'web'
 * mobile ThemeProvider: getColorTheme(mode, 'mobile', brand)
 *
 * `mode` follows the existing convention in both apps: 'light' selects the
 * light theme, anything else selects dark. `brand` selects a registered brand
 * palette (default = the canonical LearnZapp scheme); an unknown brand falls
 * back to default.
 *
 * NOTE: both apps thread `brand` through their ThemeProvider (the brand seam is
 * call-time in the providers, NOT in mobile's assets/index.js — brand
 * app_constant files import that module at module scope, so binding the brand
 * there would create an import cycle against a partially initialized
 * app_constant). Mobile's COLORS/COLORS_DARK exports remain bound to the
 * default-brand singletons for legacy non-themed importers.
 */
export function getColorTheme(mode, platform = 'web', brand = 'default') {
  const t = themesForBrand(brand);
  if (platform === 'mobile') {
    return mode === 'light' ? t.LIGHT_THEME_MOBILE : t.DARK_THEME_MOBILE;
  }
  return mode === 'light' ? t.LIGHT_THEME_WEB : t.DARK_THEME_WEB;
}
